import type { IUpdateFeaturedFacilityProcessBody } from '@retracedgmbh/modules/product-maps/contracts';
import { BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  BulkAttachCpmPayload,
  BulkAttachCpmResult,
  BulkCreateProductMapsPayload,
  BulkCreateProductMapsResult,
  BulkExportCpmPayload,
  BulkRefreshCalculationPayload,
  BulkRefreshCalculationResponse,
  CpmCollaboratorDetail,
  CpmCommunicationsResponse,
  CpmExtendPayload,
  CpmGraphResponse,
  CpmInviteCollaboratorPayload,
  CpmLinkDetailsResponse,
  CpmListBody,
  CpmListResponse,
  CpmUpdateFacilitiesPayload,
  CreateCpmPayload,
  UpdateCpmStatusPayload,
} from './types';

export async function bulkImportCpm(payload: {
  fileId: string;
}): Promise<void> {
  return apiFetch<void>(
    '/product-mapping/bulk-import',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function bulkArchiveCpm(payload: {
  ids: string[];
}): Promise<void> {
  return apiFetch<void>(
    '/product-mapping/archive',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function bulkUnarchiveCpm(payload: {
  ids: string[];
}): Promise<void> {
  return apiFetch<void>(
    '/product-mapping/unarchive',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function fetchCpmList(
  body: CpmListBody,
): Promise<CpmListResponse> {
  return apiFetch<CpmListResponse>(
    '/product-mapping/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function createCpm(
  payload: CreateCpmPayload,
): Promise<{ data: { collaboratorId: string; id: string } }> {
  return apiFetch<{ data: { collaboratorId: string; id: string } }>(
    '/product-mapping',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function bulkExportCpm(
  payload: BulkExportCpmPayload,
): Promise<{ id?: string; message?: string }> {
  return apiFetch<{ id?: string; message?: string }>(
    '/product-mapping/bulk-export',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function bulkRefreshCpmCalculation(
  payload: BulkRefreshCalculationPayload,
): Promise<{ data: BulkRefreshCalculationResponse }> {
  return apiFetch<{ data: BulkRefreshCalculationResponse }>(
    '/product-mapping/bulk-refresh-calculation',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function updateCpmStatus(
  supplyChainId: string,
  payload: UpdateCpmStatusPayload,
): Promise<void> {
  return apiFetch<void>(
    `/product-mapping/${supplyChainId}/status`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

// ─── Graph ────────────────────────────────────────────────────────────────────

export async function fetchCpmGraph(
  collaboratorId: string,
): Promise<CpmGraphResponse> {
  return apiFetch<CpmGraphResponse>(
    `/product-mapping/collaborators/${collaboratorId}/graph`,
    {},
    BACKEND_URL,
  );
}

export async function extendCpmSupplyChain(
  collaboratorId: string,
  payload: CpmExtendPayload,
): Promise<{ data: unknown }> {
  return apiFetch<{ data: unknown }>(
    `/product-mapping/collaborators/${collaboratorId}/node-extend`,
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function inviteCpmCollaborator(
  supplyChainId: string,
  payload: CpmInviteCollaboratorPayload,
): Promise<void> {
  return apiFetch<void>(
    `/product-mapping/${supplyChainId}/collaborators`,
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function attachExistingProductMap(
  collaboratorId: string,
  payload: { productMapIdToAttach: string; tracingNodeIdToAttachFrom: string },
): Promise<{ data: unknown }> {
  return apiFetch<{ data: unknown }>(
    `/product-mapping/collaborators/${collaboratorId}/attach-existing`,
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function removeCpmCollaborator(
  collaboratorId: string,
): Promise<void> {
  return apiFetch<void>(
    `/product-mapping/collaborators/${collaboratorId}`,
    { method: 'DELETE' },
    BACKEND_URL,
  );
}

export async function sendCpmReminder(
  collaboratorId: string,
  body: { status: string },
): Promise<void> {
  return apiFetch<void>(
    `/product-mapping/collaborators/${collaboratorId}/remind`,
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function acceptCpmInvitation(
  collaboratorId: string,
): Promise<void> {
  return apiFetch<void>(
    `/product-mapping/collaborators/${collaboratorId}/status`,
    { method: 'PATCH', body: JSON.stringify({ status: 'ACCEPTED' }) },
    BACKEND_URL,
  );
}

export async function updateCpmNodeFacilities(
  invokerCollaboratorId: string,
  nodeId: string,
  payload: CpmUpdateFacilitiesPayload & { supplyChainId?: string },
): Promise<void> {
  const body: IUpdateFeaturedFacilityProcessBody = {
    featuredFacilityProcessId: payload.facilityProcessIds[0] ?? '',
  };
  return apiFetch<void>(
    `/product-mapping/collaborators/${invokerCollaboratorId}/node/${nodeId}/featured-facility-process`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function deleteCpmNode(
  collaboratorId: string,
  nodeId: string,
): Promise<void> {
  return apiFetch<void>(
    `/product-mapping/collaborators/${collaboratorId}/node/${nodeId}`,
    { method: 'DELETE' },
    BACKEND_URL,
  );
}

export async function copyCpm(
  collaboratorId: string,
  payload: { name: string },
): Promise<{ data: { collaboratorId: string } }> {
  return apiFetch<{ data: { collaboratorId: string } }>(
    `/product-mapping/collaborators/${collaboratorId}/copy`,
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function renameCpm(
  supplyChainId: string,
  payload: { name: string },
): Promise<void> {
  return apiFetch<void>(
    `/product-mapping/${supplyChainId}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

// ─── Collaboration Details Sheet ──────────────────────────────────────────────

export async function fetchCpmCollaboratorDetail(
  collaboratorId: string,
): Promise<{ data: CpmCollaboratorDetail }> {
  return apiFetch<{ data: CpmCollaboratorDetail }>(
    `/product-mapping/collaborators/${collaboratorId}`,
    {},
    BACKEND_URL,
  );
}

export async function fetchCpmCommunications(
  collaboratorId: string,
  params: { page?: number; limit?: number; createdForMe?: boolean },
): Promise<CpmCommunicationsResponse> {
  return apiFetch<CpmCommunicationsResponse>(
    `/product-mapping/collaborators/${collaboratorId}/communication/list`,
    {
      method: 'POST',
      body: JSON.stringify({
        page: 1,
        limit: 100,
        createdForMe: false,
        ...params,
      }),
    },
    BACKEND_URL,
  );
}

export async function fetchCpmLinkDetails(
  linkId: string,
  supplyChainId: string,
): Promise<CpmLinkDetailsResponse> {
  return apiFetch<CpmLinkDetailsResponse>(
    `/product-mapping/link/${linkId}?supplyChainId=${supplyChainId}`,
    {},
    BACKEND_URL,
  );
}

export interface CpmNodeAsset {
  id?: string;
  name?: string | null;
  officialName?: string | null;
  country?: string | null;
  countryName?: string | null;
  facilityProcesses?: string | null;
  featuredImageFileUrl?: string | null;
  geojson?: string | null;
  companyType?: string;
}

export async function fetchCpmNodeDetails(
  supplyChainId: string,
  nodeId: string,
): Promise<{ data: CpmNodeAsset }> {
  return apiFetch<{ data: CpmNodeAsset }>(
    `/product-mapping/${supplyChainId}/node/${nodeId}/asset`,
    {},
    BACKEND_URL,
  );
}

export async function fetchCpmReviewCollaborators(
  collaboratorId: string,
  collaborationStatus?: string,
): Promise<{
  data: import('./types').CpmCollaboratorDetail[];
  metadata?: { pagination?: { total?: number } };
}> {
  return apiFetch<{
    data: import('./types').CpmCollaboratorDetail[];
    metadata?: { pagination?: { total?: number } };
  }>(
    '/product-mapping/collaborators/list',
    {
      method: 'POST',
      body: JSON.stringify({
        collaboratorId,
        ...(collaborationStatus ? { collaborationStatus } : {}),
        page: 1,
        limit: 50,
      }),
    },
    BACKEND_URL,
  );
}

export async function updateCpmCollaboratorStatus(
  collaboratorId: string,
  status: 'APPROVED' | 'REQUEST_CHANGES',
  message?: string,
): Promise<void> {
  return apiFetch<void>(
    `/product-mapping/collaborators/${collaboratorId}/status`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        status,
        ...(message !== undefined ? { message } : {}),
      }),
    },
    BACKEND_URL,
  );
}

export async function updateCpmLink(
  linkId: string,
  supplyChainId: string,
  lines: Array<{ styleId: string; quantity: number | null; unit: string }>,
  invokerCollaboratorId?: string,
): Promise<void> {
  return apiFetch<void>(
    `/product-mapping/links/${linkId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        supplyChainId,
        lines,
        ...(invokerCollaboratorId ? { invokerCollaboratorId } : {}),
      }),
    },
    BACKEND_URL,
  );
}

export async function fetchCpmLinkStyles(companyId: string): Promise<{
  data: Array<{
    id: string;
    name: string;
    codes?: Array<{ codeId: string; codeValue: string }>;
  }>;
}> {
  return apiFetch<{
    data: Array<{
      id: string;
      name: string;
      codes?: Array<{ codeId: string; codeValue: string }>;
    }>;
  }>(
    '/styles/list',
    {
      method: 'POST',
      body: JSON.stringify({
        companyId,
        isCreatedForNetwork: true,
        limit: 500,
      }),
    },
    BACKEND_URL,
  );
}

export async function submitCpmForReview(
  collaboratorId: string,
): Promise<void> {
  return apiFetch<void>(
    `/product-mapping/collaborators/${collaboratorId}/status`,
    { method: 'PATCH', body: JSON.stringify({ status: 'IN_REVIEW' }) },
    BACKEND_URL,
  );
}

export async function bulkAttachProductMaps(
  payload: BulkAttachCpmPayload,
): Promise<{ data: BulkAttachCpmResult }> {
  return apiFetch<{ data: BulkAttachCpmResult }>(
    '/product-mapping/collaborators/attach-existing',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function bulkCreateProductMaps(
  payload: BulkCreateProductMapsPayload,
): Promise<{ data: BulkCreateProductMapsResult }> {
  return apiFetch<{ data: BulkCreateProductMapsResult }>(
    '/product-mapping/bulk-create',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}
